import { useTranslation } from '@govard/i18n-core/react';
import { useState, type ReactElement } from 'react';
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  ShieldCheck,
  Mail,
  Gauge,
  Target,
  ChevronRight,
} from 'lucide-react';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import { Progress } from 'antd';
import {
  dealRiskRecommendationText,
  dealRiskSignalText,
  type RiskLabelI18n,
} from '../../lib/dealRiskLabels.js';
import {
  isWinProbabilitySignalCode,
  winProbabilityFactorText,
  type WinProbabilityFactorLike,
} from '../../../../shared/format/win-probability-labels.js';

interface DealRiskSnippetProps {
  risk: Extract<StructuredMessagePayload, { type: 'deal_risk' }>['risk'];
  onPrepareFollowUp?: ((dealId: string) => void) | undefined;
  prepareFollowUpDisabled?: boolean;
  onOpenEntity?:
    | ((type: 'deal' | 'contact', id: string, title?: string | undefined) => void)
    | undefined;
  compact?: boolean | undefined;
  className?: string | undefined;
}

const LEVEL_CONFIG: Record<
  'low' | 'medium' | 'high',
  { badgeBg: string; badgeText: string; stroke: string; iconColor: string }
> = {
  low: {
    badgeBg: 'bg-zinc-100 text-zinc-700 border-zinc-200/80',
    badgeText: 'text-zinc-700',
    stroke: 'var(--ant-color-text-secondary)',
    iconColor: 'text-zinc-500',
  },
  medium: {
    badgeBg: 'bg-amber-50/80 text-amber-800 border-amber-200/60',
    badgeText: 'text-amber-800',
    stroke: 'var(--ant-color-primary)',
    iconColor: 'text-amber-600',
  },
  high: {
    badgeBg: 'bg-rose-50 text-rose-800 border-rose-200/60',
    badgeText: 'text-rose-800',
    stroke: 'var(--ant-color-error)',
    iconColor: 'text-rose-600',
  },
};

const RISK_SCORES: Record<'low' | 'medium' | 'high', number> = {
  low: 20,
  medium: 55,
  high: 85,
};

/** Win-probability progress stroke: strong / moderate / weak outlook. */
function winProbabilityStroke(score: number): string {
  if (score >= 70) return 'var(--ant-color-success)';
  if (score >= 40) return 'var(--ant-color-primary)';
  return 'var(--ant-color-error)';
}

/**
 * Localized factor text: risk-signal codes reuse the detailed signal
 * catalog (params + pluralization), everything else goes through the shared
 * win-probability factor labels.
 */
function factorText(i18n: RiskLabelI18n, factor: WinProbabilityFactorLike): string {
  return isWinProbabilitySignalCode(factor.code)
    ? dealRiskSignalText(i18n, factor)
    : winProbabilityFactorText(i18n, factor);
}

export function DealRiskSnippet({
  risk,
  onPrepareFollowUp,
  prepareFollowUpDisabled = false,
  onOpenEntity,
  compact = false,
  className = '',
}: DealRiskSnippetProps): ReactElement {
  const { t, tc } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const levelConfig = LEVEL_CONFIG[risk.level] ?? LEVEL_CONFIG.medium;
  const riskScore = RISK_SCORES[risk.level] ?? 50;
  const recommendation = risk.recommendation
    ? dealRiskRecommendationText({ t, tc }, risk.recommendation)
    : null;

  const handleCardClick = () => {
    if (onOpenEntity) {
      onOpenEntity('deal', risk.deal.id, risk.deal.title);
    } else {
      setIsExpanded((prev) => !prev);
    }
  };

  // ─── LobeHub High-Density Row View (for Grouped Feed) ────────────────────────
  if (compact) {
    return (
      <div
        data-testid="structured-snippet-deal-risk"
        onClick={handleCardClick}
        className={`group flex items-center justify-between gap-3 px-3.5 py-2 text-xs transition-colors hover:bg-zinc-50/80 cursor-pointer ${className}`}
      >
        {/* Left: Subtle Icon + Monochrome Title + Signal Count */}
        <div className="flex min-w-0 items-center gap-2.5 flex-1">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-zinc-100/80 ${levelConfig.iconColor}`}
          >
            {risk.level === 'high' ? <AlertTriangle size={13} /> : <ShieldCheck size={13} />}
          </div>
          <span className="font-medium text-zinc-900 group-hover:text-zinc-950 truncate">
            {risk.deal.title}
          </span>
          <span className="text-[11px] text-zinc-400 shrink-0">
            • {tc('crm.chat.deal_risk.signal_count', risk.signals.length)}
          </span>
        </div>

        {/* Right: Muted Badge + Risk Meter (No wrap) + Arrow */}
        <div className="flex items-center gap-2.5 shrink-0">
          <span
            className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${levelConfig.badgeBg}`}
          >
            {t(`crm.chat.deal_risk.level.${risk.level}`)}
          </span>

          <div className="hidden sm:flex items-center gap-1.5 w-20">
            <Progress
              percent={riskScore}
              size="small"
              showInfo={false}
              strokeColor={levelConfig.stroke}
              railColor="var(--ant-color-fill-quaternary)"
              className="m-0 flex-1"
            />
            <span className="text-[11px] font-mono font-medium text-zinc-600 w-8 text-right whitespace-nowrap shrink-0">
              {riskScore}%
            </span>
          </div>

          <ChevronRight
            size={13}
            className="text-zinc-400 group-hover:text-zinc-700 transition-transform group-hover:translate-x-0.5"
          />
        </div>
      </div>
    );
  }

  // ─── Standalone Detailed Card View ───────────────────────────────────────────
  return (
    <div
      data-testid="structured-snippet-deal-risk"
      className={`my-1.5 overflow-hidden rounded-xl border border-zinc-200/90 bg-white transition-all hover:border-zinc-300 shadow-2xs ${className}`}
    >
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex cursor-pointer items-center justify-between gap-2.5 px-3.5 py-2.5 text-xs hover:bg-zinc-50/80 group"
      >
        <div className="flex min-w-0 items-center gap-2 flex-1">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-zinc-100 ${levelConfig.iconColor}`}
          >
            {risk.level === 'high' ? <AlertTriangle size={13} /> : <ShieldCheck size={13} />}
          </div>
          <span
            onClick={(e) => {
              if (onOpenEntity) {
                e.stopPropagation();
                onOpenEntity('deal', risk.deal.id, risk.deal.title);
              }
            }}
            className="font-semibold text-zinc-900 hover:underline truncate"
          >
            {risk.deal.title}
          </span>
          <span className="text-zinc-300 shrink-0">•</span>
          <span className={`text-[11px] font-medium shrink-0 ${levelConfig.badgeText}`}>
            {t(`crm.chat.deal_risk.level.${risk.level}`)}
          </span>
          <span className="text-[11px] text-zinc-400 shrink-0">({riskScore}%)</span>
        </div>

        <button
          type="button"
          className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 hover:text-zinc-800"
        >
          <span>
            {isExpanded ? t('crm.ai_chat.widget.collapse') : t('crm.ai_chat.widget.expand')}
          </span>
          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {isExpanded && (
        <div className="border-t border-zinc-100 p-3.5 text-xs space-y-3 bg-white">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 shrink-0 whitespace-nowrap text-zinc-500 font-medium text-[11px]">
              <Gauge size={12} className={levelConfig.iconColor} />
              <span>{t('crm.snippets.deal_risk.loss_risk')}</span>
              <span className={`font-bold ${levelConfig.badgeText}`}>{riskScore}%</span>
            </div>
            <div className="flex-1 max-w-[100px]">
              <Progress
                percent={riskScore}
                size="small"
                showInfo={false}
                strokeColor={levelConfig.stroke}
                railColor="var(--ant-color-fill-quaternary)"
                className="m-0"
              />
            </div>
          </div>

          {risk.winProbability && (
            <div className="space-y-1.5" data-testid="deal-risk-win-probability">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 shrink-0 whitespace-nowrap text-zinc-500 font-medium text-[11px]">
                  <Target size={12} className="text-zinc-400" />
                  <span>{t('crm.win_probability.title')}</span>
                  <span
                    data-testid="deal-risk-win-probability-score"
                    className="font-bold text-zinc-800 font-mono"
                  >
                    {risk.winProbability.score}%
                  </span>
                </div>
                <div className="flex-1 max-w-[100px]">
                  <Progress
                    percent={risk.winProbability.score}
                    size="small"
                    showInfo={false}
                    strokeColor={winProbabilityStroke(risk.winProbability.score)}
                    railColor="var(--ant-color-fill-quaternary)"
                    className="m-0"
                  />
                </div>
              </div>
              <ul className="space-y-1 text-zinc-600">
                {risk.winProbability.factors.map((factor, idx) => (
                  <li key={idx} className="flex gap-1.5 items-start justify-between">
                    <span className="min-w-0">{factorText({ t, tc }, factor)}</span>
                    {factor.impact !== 0 ? (
                      <span
                        className={`shrink-0 font-mono text-[11px] font-medium ${
                          factor.impact > 0 ? 'text-emerald-700' : 'text-rose-700'
                        }`}
                      >
                        {factor.impact > 0 ? `+${factor.impact}` : factor.impact}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {risk.signals.length > 0 && (
            <div className="space-y-1.5">
              <ul className="space-y-1 text-zinc-600">
                {risk.signals.map((sig, idx) => (
                  <li key={idx} className="flex gap-1.5 items-start">
                    <span className="text-zinc-400 mt-[2px]">•</span>
                    <span>{dealRiskSignalText({ t, tc }, sig)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {recommendation && (
            <div className="rounded-lg bg-zinc-50 p-2.5 text-zinc-900 border border-zinc-200/80 mt-1">
              <p className="text-[11px] font-semibold text-zinc-800">{recommendation.title}</p>
              <p className="text-[11px] text-zinc-600 mt-0.5">{recommendation.description}</p>
            </div>
          )}

          {onPrepareFollowUp && risk.recommendation?.channel === 'email' && (
            <button
              type="button"
              data-testid="deal-risk-prepare-follow-up"
              disabled={prepareFollowUpDisabled}
              onClick={() => onPrepareFollowUp(risk.deal.id)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-zinc-800 disabled:opacity-50 cursor-pointer mt-1"
            >
              <Mail size={13} />
              {t('crm.chat.deal_risk.prepare_follow_up')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
